import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, Search, Download, ArrowUpRight } from "lucide-react";
import { BANDS } from "@/lib/trcaSchema";

const BAND_PILL = (band) => {
  const b = BANDS.find((x) => x.name === band);
  if (!b) return "bg-secondary text-ink-muted border-ink-line";
  if (b.name === "Emerging") return "bg-warn-soft text-warn border-warn/30";
  if (b.name === "Developing") return "bg-warn-soft text-warn border-warn/30";
  if (b.name === "Established") return "bg-brand-soft text-brand border-brand/30";
  return "bg-good-soft text-good border-good/30";
};

export default function TrcaSubmissions() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [band, setBand] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (band) params.set("band", band);
      const { data } = await api.get(`/trca/submissions?${params.toString()}`);
      setItems(data.items);
      setStats(data.stats);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [band]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const downloadAll = async () => {
    try {
      const resp = await api.get(`/trca/submissions/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = "trca_submissions.csv";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  return (
    <div className="space-y-8" data-testid="trca-submissions-page">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-ink-line pb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-brand text-white grid place-items-center rounded-sm">
            <ClipboardList className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Lead Intelligence</div>
            <h1 className="font-display text-4xl font-bold tracking-tight mt-1">TRCA Submissions</h1>
            <p className="text-sm text-ink-muted mt-1.5">Inbound capability assessments from prospective clients.</p>
          </div>
        </div>
        <button
          onClick={downloadAll}
          data-testid="trca-export-all-btn"
          className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-4 py-2.5 text-sm font-medium rounded-sm transition-colors"
        >
          <Download className="w-4 h-4" /> Export all CSV
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="card-flat p-4">
            <div className="stat-label">Total</div>
            <div className="font-num text-3xl font-bold mt-2">{stats.total}</div>
          </div>
          <div className="card-flat p-4">
            <div className="stat-label">Avg score</div>
            <div className="font-num text-3xl font-bold mt-2 text-brand">{stats.avg_score || 0}</div>
          </div>
          {BANDS.map((b) => (
            <div key={b.name} className="card-flat p-4" data-testid={`band-count-${b.name.toLowerCase()}`}>
              <div className="stat-label">{b.name}</div>
              <div className="font-num text-3xl font-bold mt-2" style={{ color: b.color }}>{stats.band_counts?.[b.name] || 0}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="trca-search"
            placeholder="Search by name, company, email, phone…"
            className="w-full pl-9 pr-3 py-2 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
          />
        </div>
        <div className="flex gap-0 border border-ink-line rounded-sm bg-white overflow-hidden">
          <button onClick={() => setBand("")} data-testid="band-filter-all" className={`px-3 py-2 text-[12px] uppercase tracking-wider border-r border-ink-line ${band === "" ? "bg-brand text-white font-medium" : "text-ink-muted hover:bg-secondary"}`}>All</button>
          {BANDS.map((b, i) => (
            <button
              key={b.name}
              onClick={() => setBand(b.name)}
              data-testid={`band-filter-${b.name.toLowerCase()}`}
              className={`px-3 py-2 text-[12px] uppercase tracking-wider ${i < BANDS.length - 1 ? "border-r border-ink-line" : ""} ${band === b.name ? "bg-brand text-white font-medium" : "text-ink-muted hover:bg-secondary"}`}
            >
              {b.name}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary border-b border-ink-line">
            <tr className="text-left text-[11px] uppercase tracking-wider text-ink-muted">
              <th className="px-5 py-3 font-medium">Submitted</th>
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Company</th>
              <th className="px-5 py-3 font-medium">Email</th>
              <th className="px-5 py-3 font-medium">Phone</th>
              <th className="px-5 py-3 font-medium text-right">Score</th>
              <th className="px-5 py-3 font-medium">Band</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={8} className="px-5 py-10 text-center text-ink-muted uppercase tracking-wider text-xs">Loading…</td></tr>)}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-10 text-center text-ink-muted">No submissions yet.</td></tr>
            )}
            {items.map((s) => (
              <tr key={s.id} className="border-b border-ink-line last:border-b-0 hover:bg-secondary/60 transition-colors">
                <td className="px-5 py-3 text-ink-muted font-num text-xs">
                  {new Date(s.submitted_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                </td>
                <td className="px-5 py-3 font-medium">{s.name}</td>
                <td className="px-5 py-3">{s.company_name}</td>
                <td className="px-5 py-3 text-ink-muted font-num text-xs">{s.email}</td>
                <td className="px-5 py-3 text-ink-muted font-num text-xs">{s.phone}</td>
                <td className="px-5 py-3 text-right font-num font-bold text-base">{s.total_score}</td>
                <td className="px-5 py-3"><span className={`chip ${BAND_PILL(s.band)}`} data-testid={`row-band-${s.id}`}>{s.band}</span></td>
                <td className="px-5 py-3 text-right">
                  <Link
                    to={`/admin/trca/${s.id}`}
                    data-testid={`view-submission-${s.id}`}
                    className="inline-flex items-center gap-1 text-brand hover:text-brand-hover text-[13px] font-medium"
                  >
                    View <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
